import { Box, Divider, List } from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useEkko from '../Providers/EkkoProvider';
import useNavigation from '../Providers/NavigationProvider';
import { NavHeader } from './Navigation/NavHeader';
import { NavGroup } from './Navigation/NavGroup';
import { NavItem } from './Navigation/NavItem';
import { findActiveSubMenu } from './Navigation/routeMatching';

export const NavigationDrawer = (): React.JSX.Element => {
  const { sortedMenuList: subMenuStateList, setActiveSubMenu } = useNavigation();
  const { config } = useEkko();
  const { pathname } = useLocation();

  const [isSortingEnabled, setSortingEnabled] = useState(false);

  // Sync the active submenu to the current route. Lives here (not in
  // NavigationProvider) because the provider mounts above the Router and
  // useLocation would throw there. Routes outside any submenu (e.g. /me/*)
  // resolve to undefined, which collapses the previously-active section
  // while leaving the user's one manually-opened section alone.
  useEffect(() => {
    setActiveSubMenu(findActiveSubMenu(pathname));
  }, [pathname, setActiveSubMenu]);

  return (
    <Box
      height={'100vh'}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: 'fit-content',
        flexShrink: 0,
        overflowX: 'hidden',
        whiteSpace: 'nowrap',
        backgroundColor: 'background.paper',
        // Docked sidebar: a thin divider border reads cleaner than a heavy
        // floating drop shadow.
        borderRight: '1px solid',
        borderColor: 'divider',
      }}
    >
      <NavHeader isSortingEnabled={isSortingEnabled} onToggleSorting={() => setSortingEnabled(!isSortingEnabled)} />
      <Divider />
      {/* Scroll area: takes the remaining vertical space and scrolls
          ONLY when content overflows. Scrollbars are hidden so the
          drawer looks clean either way. Inside, FAQ uses `mt: auto`
          to drop to the bottom when there's free space — when content
          overflows, the auto margin collapses to 0 and FAQ flows
          naturally at the end (and scrolls with the rest). */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <List disablePadding>
          {subMenuStateList.map((state) => (
            <NavGroup
              key={state.subMenu}
              label={state.subMenu}
              subMenu={state.subMenu}
              isSortingEnabled={isSortingEnabled}
              accessKeys={state.accessKeys}
            />
          ))}
        </List>
        {config.faqUrl !== undefined && (
          <Box sx={{ mt: 'auto' }}>
            <NavItem icon={<HelpOutline />} label="FAQ" href={config.faqUrl} />
          </Box>
        )}
      </Box>
    </Box>
  );
};
